/**
 * Call speech-to-text on iOS and Android: the shared native recognizer (nativeSpeech.ts) kept listening
 * for the whole call. Live partials are the captions; the turn ends when they settle (turnDetector.ts) or
 * when the recognizer itself decides the utterance is over (Android's silence end, an iOS final result).
 * When the recognizer stops on its own with nothing heard, it is restarted while the call wants to listen.
 */
import type { SttEngine, SttHandlers } from '../types'
import { joinText, nativeSpeech, SpeechStartError, type NativeSpeech, type SpeechEnd } from './nativeSpeech'
import { TurnDetector } from './turnDetector'

export interface NativeCallSttOptions {
    speech?: NativeSpeech
    /** Names to favour (the assistant's name, brands). */
    contextualStrings?: string[]
    now?: () => number
}

export function createNativeCallStt(h: SttHandlers, opts: NativeCallSttOptions = {}): SttEngine {
    const speech = opts.speech ?? nativeSpeech()
    const now = opts.now ?? (() => performance.now())
    const owner = speech.owner('voice-call')
    const detector = new TurnDetector()
    let want: 'listen' | 'paused' | 'stopped' = 'stopped'
    let running = false
    /** Text of earlier recognizer sessions within this turn (it restarted mid-turn). */
    let turnText = ''
    let sessionText = ''
    let ticker: ReturnType<typeof setInterval> | null = null
    let restartTimer: ReturnType<typeof setTimeout> | null = null
    let ended = false
    /** The recognizer session this engine currently owns (each begin() makes a new one). */
    let seq = 0
    let current = 0

    const full = () => joinText(turnText, sessionText)

    function resetTurn() {
        turnText = ''
        sessionText = ''
        ended = false
        detector.reset()
    }

    function endTurn() {
        if (ended || !full()) return
        ended = true
        h.endOfTurn()
    }

    function onText(t: string) {
        if (want !== 'listen' || ended) return
        const wasQuiet = !detector.speaking
        sessionText = t
        const text = full()
        detector.onPartial(text, now())
        if (wasQuiet && detector.speaking) h.speechStart()
        h.partial(text)
    }

    function onLevel(l: number) {
        detector.onLevel(l, now())
        h.level(l)
    }

    function onEnd(end: SpeechEnd) {
        running = false
        if (!end.auto) return
        turnText = joinText(turnText, end.text || sessionText)
        sessionText = ''
        if (end.error?.kind === 'permission') {
            h.error('permission', end.error.message)
            return
        }
        if (want !== 'listen') return
        // The recognizer ended the utterance itself: that's the end of the turn.
        if (full() && !ended) {
            endTurn()
            return
        }
        scheduleRestart(end.reason === 'error' ? 600 : 120)
    }

    function scheduleRestart(ms: number) {
        if (restartTimer) clearTimeout(restartTimer)
        restartTimer = setTimeout(() => {
            restartTimer = null
            void begin()
        }, ms)
    }

    async function begin(): Promise<void> {
        if (running || want !== 'listen') return
        running = true
        const my = ++seq
        current = my
        // A superseded session (still ending while this one starts) must not clear `running` or feed this turn.
        const mine = () => my === current
        try {
            await speech.start(owner, {
                onText: (t) => { if (mine()) onText(t) },
                onLevel: (l) => { if (mine()) onLevel(l) },
                onEnd: (e) => { if (mine()) onEnd(e) },
            }, { contextualStrings: opts.contextualStrings })
            if (!mine()) {
                // Halted while it started (nothing newer took over): don't leave it listening.
                if (!current) void speech.cancel(owner)
                return
            }
            if (want !== 'listen') {
                void speech.cancel(owner)
                return
            }
            h.ready()
        } catch (e) {
            if (!mine()) throw e
            running = false
            const err = e instanceof SpeechStartError ? e.error : null
            h.error(err?.kind === 'permission' ? 'permission' : err?.kind === 'network' ? 'network' : 'unavailable', err?.message)
            throw e
        }
    }

    function startTicker() {
        if (ticker) return
        ticker = setInterval(() => {
            if (want === 'listen' && !ended && detector.due(now())) endTurn()
        }, 100)
    }

    function halt() {
        if (restartTimer) clearTimeout(restartTimer)
        restartTimer = null
        current = 0
        // Always: it's a no-op unless this engine's session is the active one, and a flag out of sync
        // must never leave the mic on.
        void speech.cancel(owner)
        running = false
    }

    return {
        kind: 'native',
        liveCaptions: true,
        async start() {
            want = 'listen'
            resetTurn()
            startTicker()
            await begin()
        },
        pause() {
            want = 'paused'
            resetTurn()
            halt()
        },
        resume() {
            if (want === 'listen' && (running || restartTimer)) return
            want = 'listen'
            resetTurn()
            startTicker()
            void begin().catch(() => {})
        },
        async finish() {
            current = 0
            const last = speech.isActive(owner) ? await speech.stop(owner) : ''
            running = false
            const text = joinText(turnText, last || sessionText)
            resetTurn()
            // The mic stays closed until the call resumes it (after the turn is sent).
            want = 'paused'
            return text
        },
        stop() {
            want = 'stopped'
            if (ticker) clearInterval(ticker)
            ticker = null
            resetTurn()
            halt()
        },
    }
}
